export const LAST_ACTIVE_PLACEHOLDER = '–';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const ABSOLUTE_DATE_AFTER_DAYS = 7;

export enum LastActiveUnit {
  DAYS = 'days',
  HOURS = 'hours',
  JUST_NOW = 'just-now',
  MINUTES = 'minutes',
}

// How long ago, for the page to put into words in its own language; an absolute date past a week.
export type LastActive = { count: number; unit: LastActiveUnit } | { date: string };

const pad = (value: number): string => String(value).padStart(2, '0');

const toAbsoluteDate = (value: Date): string =>
  `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}`;

/**
 * Last Active on the People-with-Access list, per 20.2.4.1 section 3 — and every other "when" the
 * data room and the workspaces show, so they all read alike.
 *
 * Null is not "never had access" — it means this person has not opened this particular room yet,
 * which is true regardless of their access status. Someone can be Active and still have no date,
 * and a revoked person keeps whatever timestamp they had.
 *
 * Past a week the relative form stops being useful ("5 weeks ago" tells an Admin nothing they can
 * act on), so it switches to an absolute date.
 */
export const lastActiveOf = (
  lastActiveAt: Date | null | string | undefined,
  now: Date = new Date(),
): LastActive | null => {
  if (!lastActiveAt) return null;

  const value = lastActiveAt instanceof Date ? lastActiveAt : new Date(lastActiveAt);
  if (Number.isNaN(value.getTime())) return null;

  const elapsed = now.getTime() - value.getTime();

  // A clock skew between server and browser must not read as "in 3 minutes".
  if (elapsed < MINUTE_MS) return { count: 0, unit: LastActiveUnit.JUST_NOW };

  if (elapsed < HOUR_MS) {
    return { count: Math.floor(elapsed / MINUTE_MS), unit: LastActiveUnit.MINUTES };
  }

  if (elapsed < DAY_MS) {
    return { count: Math.floor(elapsed / HOUR_MS), unit: LastActiveUnit.HOURS };
  }

  const days = Math.floor(elapsed / DAY_MS);
  if (days < ABSOLUTE_DATE_AFTER_DAYS) {
    return { count: days, unit: LastActiveUnit.DAYS };
  }

  return { date: toAbsoluteDate(value) };
};
